import moment from 'moment';
import { Campaign, CampaignBase, CampaignChannel, CampaignConsentMode, CampaignGoal, CampaignReduced, CampaignStatus } from '../../models';
import { TK } from '../../store/translations/translationKeys';

export const buildNewCampaignBase = (overrides: Partial<CampaignBase> = {}): CampaignBase => ({
    name: '',
    subject: '',
    content: '',
    channel: CampaignChannel.Email,
    goal: CampaignGoal.Custom,
    consentMode: CampaignConsentMode.ApprovedOnly,
    builderStep: 1,
    includeClientsWithoutMarketingConsentSubmitted: false,
    ...overrides,
});

export const buildNewCampaign = (overrides: Partial<Campaign> = {}): Campaign => ({
    ...buildNewCampaignBase(),
    id: '',
    noOfRecipients: 0,
    status: CampaignStatus.Draft,
    reTriggerCooldownDays: 30,
    ...overrides,
});

export const formatCampaignDateTime = (value?: string): string => value ? value.replace('T', ' ').slice(0, 16) : '';

export const getCampaignNow = (): string => moment().format('YYYY-MM-DDTHH:mm:ss');

export const getCampaignStatusTranslationKey = (status: CampaignStatus): TK => {
    switch (status) {
        case CampaignStatus.Draft: return TK.campaignStatusDraft;
        case CampaignStatus.Ready: return TK.campaignStatusReady;
        case CampaignStatus.AwaitingPayment: return TK.campaignStatusAwaitingPayment;
        case CampaignStatus.Paid: return TK.campaignStatusPaid;
        case CampaignStatus.Scheduled: return TK.campaignStatusScheduled;
        case CampaignStatus.Sending: return TK.campaignStatusSending;
        case CampaignStatus.Sent: return TK.campaignStatusSent;
        case CampaignStatus.PartiallyFailed: return TK.campaignStatusPartiallyFailed;
        case CampaignStatus.Failed: return TK.campaignStatusFailed;
        case CampaignStatus.Archived: return TK.campaignStatusArchived;
        default: return TK.unknown;
    }
}

export const campaignStatusColor = (status: CampaignStatus): string => {
    switch (status) {
        case CampaignStatus.Draft: return 'var(--text-muted)';
        case CampaignStatus.Ready: return 'var(--appBlue)';
        case CampaignStatus.AwaitingPayment: return 'var(--warning-color)';
        case CampaignStatus.Paid: return 'var(--info-color)';
        case CampaignStatus.Scheduled: return 'var(--success-color)';
        case CampaignStatus.Sending: return 'var(--warning-color)';
        case CampaignStatus.Sent: return 'var(--success-color)';
        case CampaignStatus.PartiallyFailed: return 'var(--warning-color)';
        case CampaignStatus.Failed: return 'var(--danger-color)';
        case CampaignStatus.Archived: return 'var(--text-muted)';
        default: return 'var(--text-muted)';
    }
}

export const campaignStatusBackground = (status: CampaignStatus): string => {
    switch (status) {
        case CampaignStatus.Draft: return 'rgba(108, 117, 125, 0.12)';
        case CampaignStatus.Ready: return 'rgba(42, 183, 175, 0.14)';
        case CampaignStatus.AwaitingPayment: return 'rgba(255, 193, 7, 0.18)';
        case CampaignStatus.Paid: return 'rgba(0, 147, 133, 0.14)';
        case CampaignStatus.Scheduled: return 'rgba(42, 183, 175, 0.14)';
        case CampaignStatus.Sending: return 'rgba(255, 193, 7, 0.18)';
        case CampaignStatus.Sent: return 'rgba(0, 147, 133, 0.14)';
        case CampaignStatus.PartiallyFailed: return 'rgba(255, 193, 7, 0.18)';
        case CampaignStatus.Failed: return 'rgba(220, 53, 69, 0.14)';
        case CampaignStatus.Archived: return 'rgba(108, 117, 125, 0.12)';
        default: return 'rgba(108, 117, 125, 0.12)';
    }
}

export const campaignChannelLabel = (channel: CampaignChannel): TK => {
    switch (channel) {
        case CampaignChannel.WhatsApp: return TK.whatsApp;
        case CampaignChannel.SMS: return TK.sms;
        default: return TK.email;
    }
};

export const campaignGoalLabel = (goal: CampaignGoal): TK => {
    switch (goal) {
        case CampaignGoal.ReactivateClients: return TK.campaignGoalReactivateClients;
        case CampaignGoal.PromoteService: return TK.campaignGoalPromoteService;
        case CampaignGoal.AnnounceNews: return TK.campaignGoalAnnounceNews;
        case CampaignGoal.BirthdayOrSeasonal: return TK.campaignGoalBirthdayOrSeasonal;
        case CampaignGoal.Custom:
        default:
            return TK.campaignGoalCustom;
    }
}

export const getCampaignExecutionSummary = (campaign: CampaignReduced): { labelKey: TK; value: string } => {
    if (!campaign.lastTriggeredAt) {
        return { labelKey: TK.campaignNeverSent, value: '' };
    }

    return {
        labelKey: getCampaignStatusTranslationKey(campaign.status),
        value: formatCampaignDateTime(campaign.lastTriggeredAt),
    };
}

export const normalizeCampaignSearch = (value: string): string => value.trim().toLowerCase();
